import { schemaMetadataFileName, version1PathPreferences, type PathPreferenceSpec } from "@gadgetinc/conventions";
import path from "path";
import { ReservedTopLevelNames, ValidGraphQLIdentifier } from "../EditValidations";
import { getNamespace } from "../FilesystemIntrospector";
import { WithoutProblems } from "../getEnvironmentIndex";
import { SourceFileIndexEntry } from "../IndexEntry";
import { getPotentialNamespaceProblems } from "../problems/finders/NamespaceProblems";

export const jsOrTsFileSet = new Set([".js", ".ts", ".mjs", ".mts", ".cjs", ".cts", ".tsx", ".jsx"]);

export const isJavascriptExtension = (extension: string) => {
  return jsOrTsFileSet.has(extension);
};

export const isExecutableJsOrTsFile = (basename: string, extension: string = path.extname(basename)) => {
  return isJavascriptExtension(extension) && !basename.endsWith(".d.ts");
};

export const globalActionsDir = "api/actions/";
export const modelsDir = "api/models/";
const schemaFile = `/${schemaMetadataFileName}`;
const numbers = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

export const schemaFileDirParts = (path: string): string[] | null => {
  if (!path.startsWith(modelsDir) || !path.endsWith(schemaFile)) {
    return null;
  }

  const dirNames = path.slice(modelsDir.length, -schemaFile.length);
  if (dirNames == "") return null;
  return dirNames.split("/");
};

export const fileIsNamespacedModelSchemaMetadata = (file: { path: string }) => {
  const parts = schemaFileDirParts(file.path);
  return Boolean(parts);
};

export const fileIsNonNamespacedModelSchemaMetadata = (file: { path: string }) => {
  const parts = schemaFileDirParts(file.path);
  if (!parts) return false;
  return parts.length == 1 && !numbers.includes(parts[0][0]);
};

export const fileIsGlobalAction = (file: SourceFileIndexEntry, canHaveNamespacedModels: boolean) => {
  if (!file.path.startsWith(version1PathPreferences.globalActionsFolderPath)) {
    return false;
  }

  const paths = file.path.split("/");

  if (!file.isExecutableJsOrTsFile || (!canHaveNamespacedModels && paths.length !== 3)) {
    return false;
  }

  // Possibly `globalActions/{action}.{valid_extension}` path structure
  const namespaceProblems = getPotentialNamespaceProblems({ supportNamespace: canHaveNamespacedModels, namespace: paths.slice(2, -1) });
  if (namespaceProblems.length) {
    return; // Cannot be a GlobalAction with invalid namespace segments
  }

  const actionApiIdentifier = path.basename(file.path, file.extension);

  const apiIdIsValidGQL = ValidGraphQLIdentifier.test(actionApiIdentifier);
  const apiIdIsReservedName = ReservedTopLevelNames.includes(actionApiIdentifier);

  return apiIdIsValidGQL && !apiIdIsReservedName;
};

export const fileIsGlobalView = (
  file: { path: string },
  canHaveNamespacedModels: boolean,
  basePath: string = version1PathPreferences.computedViewsPath
) => {
  if (!file.path.startsWith(basePath)) {
    return false;
  }

  const extension = path.extname(file.path);
  if (extension != ".gelly") {
    return false;
  }

  const paths = file.path.split("/");
  if (!canHaveNamespacedModels && paths.length !== 3) {
    return false;
  }

  const namespaceProblems = getPotentialNamespaceProblems({ supportNamespace: canHaveNamespacedModels, namespace: paths.slice(2, -1) });
  if (namespaceProblems.length) {
    return false; // Cannot be a global view with invalid namespace segments
  }

  const viewApiIdentifier = path.basename(file.path, extension);

  const apiIdIsValidGQL = ValidGraphQLIdentifier.test(viewApiIdentifier);
  const apiIdIsReservedName = ReservedTopLevelNames.includes(viewApiIdentifier);

  return apiIdIsValidGQL && !apiIdIsReservedName;
};

/**
 * Returns true if the given file is a valid model action file relative to the given schema file path
 * Validates that the file name is valid
 **/
export const fileIsModelAction = (file: WithoutProblems<SourceFileIndexEntry>, modelFolder: string) => {
  if (file.dirname !== `${modelFolder}/actions`) return false;
  return ValidGraphQLIdentifier.test(file.parsedPath.name) && file.isExecutableJsOrTsFile;
};

export const fileIsModelView = (file: WithoutProblems<SourceFileIndexEntry>, modelFolder: string) => {
  if (file.dirname !== `${modelFolder}/views`) return false;
  return ValidGraphQLIdentifier.test(file.parsedPath.name) && file.extension == ".gelly";
};

/**
 * Returns true if the given file path looks like it should be a model action.
 * Doesn't check if the action API identifier is valid or if the model actually exists
 **/
export const filePathLooksLikeModelAction = (filePath: string, pathPreferences: PathPreferenceSpec) => {
  if (!filePath.startsWith(pathPreferences.modelsFolderPath)) return false;
  const segments = filePath.split("/");
  const actionsFolderIndex = segments.indexOf("actions");
  // if the file isn't in an actions folder, or isn't in an actions folder within a model folder, it's not a model action
  if (actionsFolderIndex == -1 || actionsFolderIndex == 0) return false;
  return isExecutableJsOrTsFile(filePath);
};

export const filePathLooksLikeModelView = (filePath: string) => {
  if (!filePath.startsWith(modelsDir)) return false;
  const segments = filePath.split("/");
  const subdir = segments[segments.length - 2]; // take the second last segment
  return subdir == "views" && path.extname(filePath) == ".gelly";
};

export const fileIsBackendRoute = (filePath: string, pathPreferences: PathPreferenceSpec) => {
  return pathPreferences.backendRouteFileRegex.test(filePath);
};

export const fileIsStructuredFrontendRoute = (filePath: string, pathPreferences: PathPreferenceSpec) => {
  return filePath.startsWith(pathPreferences.frontendRoutesFolder);
};

export const fileIsBootPlugin = (filePath: string, pathPreferences: PathPreferenceSpec) => {
  return filePath.startsWith(pathPreferences.bootPluginsPath);
};

export const schemaFilePathToModelIdentifier = (schemaFilePath: string) => {
  const parts = schemaFileDirParts(schemaFilePath);
  if (!parts) return;

  return {
    apiIdentifier: parts[parts.length - 1],
    namespace: parts.length > 1 ? getNamespace(parts.slice(0, -1).join("/")) : [],
  };
};

export const getFolderPathEndingWithSlash = (folderPath: string) => {
  return folderPath.endsWith("/") ? folderPath : `${folderPath}/`;
};

export const convertTsToJsFileName = (filePath: string) => {
  return filePath.replace(/\.tsx?$|\.ts$|\.mts$|\.cts$/g, (match) => {
    switch (match) {
      case ".tsx":
        return ".jsx";
      case ".mts":
        return ".mjs";
      case ".cts":
        return ".cjs";
      default:
        return ".js";
    }
  });
};
